import { FC, useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useSelector } from "react-redux";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { tAddressPayload, tLongAddress } from "tmt_api/src/types/types.validator";

import { getAdminRetailerAddresses, getRetailerAddresses, isApiError } from "src/util/apiNew";
import { useQuery } from "src/hooks/api";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { tModalComponentProps } from "src/redux/modals/types";
import { addRetailerAddress, removeRetailerAddress, updateRetailerAddress } from "src/redux/user/actions";
import { caProvinceOptions, usStateOptions } from "src/util/comboBoxData";
import { formatPhoneNumber, formatShortAddress } from "src/util/gFormatting";
import { PdFieldset, PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdIconButton } from "src/components/forms/icon-button";
import {
  PdHookFormErrorMessage,
  PdHookFormInput,
  PdHookFormInputPhone,
  PdHookFormSelect,
  PdHookFormSwitch,
} from "src/components/hook-form";
import { PdArrowLeft, PdCheck, PdPencilLine, PdTrash } from "src/components/Icons";
import { PdStack } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { tPressableState } from "src/components/types";
import { useAppDispatch } from "src/redux";

import useConfirmModal from "./useConfirmModal";
import { tPdSelect } from "src/components/forms/select";
import { tPdInputProps } from "src/components/forms/input";
import { useIsMobile } from "../ui";

type tCountryCodes = "US" | "CA";

export type tCountryOption = { label: string; value: tCountryCodes };

export type tRetailerAddressesModalOptions = {
  selectedId?: string;
  retailerId?: string;
};

export type tRetailerAddressesModalResult = tLongAddress;

export type tRetailerAddressesFormData = {
  id: string;
  isDefault: boolean;
  name: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: { value: string; label: string } | null;
  zipCode: string;
  country: tCountryOption | null;
};

const usCountryOption: tCountryOption = { label: "United States", value: "US" };
const caCountryOption: tCountryOption = { label: "Canada", value: "CA" };

const countryOptions = [usCountryOption, caCountryOption];

const useIsMobileSelector = () => {
  return useSelector(
    (state) => state.NavigationReducer.deviceSize === "xs" || state.NavigationReducer.deviceSize === "sm",
  );
};

const getTitle = (address: tRetailerAddressesFormData | undefined) => {
  if (address?.id !== undefined) return "Edit an Address";
  if (address) return "Add an Address";
  return "Shipping Addresses";
};

const AddressesList: FC<{
  data?: tLongAddress[];
  selectedId?: string;
  onSelect: (address: tLongAddress) => void;
  onAdd: () => void;
  onEdit: (address: tLongAddress) => void;
  onDelete: (address: tLongAddress) => void;
}> = ({ data, selectedId, onSelect, onAdd, onEdit, onDelete }) => {
  return (
    <>
      {data?.map?.((address) => {
        const isSelected = address.id === selectedId;
        return (
          <Pressable
            key={`${address.address1}-${address.state_or_province}-${address.postal_code}`}
            disabled={selectedId === undefined}
            onPress={() => onSelect(address)}
            style={styles.addressButton}
          >
            {({ pressed, hovered }: tPressableState) => {
              return (
                <View
                  style={[
                    styles.addressButtonContent,
                    pressed ? styles.addressButtonPressedContent : {},
                    hovered ? styles.addressButtonHoveredContent : {},
                    isSelected ? styles.addressButtonSelectedContent : {},
                  ]}
                >
                  {!!address.isDefault && (
                    <View style={styles.addressDefaultBadge}>
                      <Text style={styles.addressDefaultBadgeLabel}>Default address</Text>
                      <PdCheck style={styles.addressDefaultBadgeIcon} color={kColors.brandPurple} />
                    </View>
                  )}
                  <View style={styles.addressButtonGroup}>
                    <PdIconButton
                      onPress={() => onEdit(address)}
                      size="sm"
                      variant="ghost"
                      icon={<PdPencilLine style={styles.addressIcon} />}
                    />
                    {data.length > 1 && (
                      <PdIconButton
                        onPress={() => onDelete(address)}
                        size="sm"
                        variant="ghost"
                        icon={<PdTrash style={styles.addressIcon} />}
                      />
                    )}
                  </View>
                  {!!address.name && (
                    <Text style={[styles.addressText, styles.addressTextPrimary]}>{address.name}</Text>
                  )}
                  <Text style={styles.addressText}>{address.address1}</Text>
                  {!!address.address2 && <Text style={styles.addressText}>{address.address2}</Text>}
                  <Text style={styles.addressText}>{formatShortAddress(address)}</Text>
                  {!!address.phone && <Text style={styles.addressText}>{formatPhoneNumber(address.phone)}</Text>}
                </View>
              );
            }}
          </Pressable>
        );
      })}
      <PdButton onPress={onAdd}>
        <PdButtonLabel>Add an address</PdButtonLabel>
      </PdButton>
    </>
  );
};

const USStatesSelect: FC<{ onSubmitEditing?: tPdSelect["onSubmitEditing"] }> = ({ onSubmitEditing }) => {
  return (
    <>
      <PdFormLabel>State *</PdFormLabel>
      <PdHookFormSelect
        name="state"
        rules={{ required: "Required" }}
        placeholder="State"
        options={usStateOptions}
        isClearable={false}
        onSubmitEditing={onSubmitEditing}
      />
      <PdHookFormErrorMessage name="state" />
    </>
  );
};

const CAProvincesSelect: FC<{ onSubmitEditing?: tPdSelect["onSubmitEditing"] }> = ({ onSubmitEditing }) => {
  return (
    <>
      <PdFormLabel>Province *</PdFormLabel>
      <PdHookFormSelect
        name="state"
        rules={{ required: "Required" }}
        placeholder="Province"
        options={caProvinceOptions}
        isClearable={false}
        onSubmitEditing={onSubmitEditing}
      />
      <PdHookFormErrorMessage name="state" />
    </>
  );
};

const ZipCodeInput: FC<{ onSubmitEditing?: tPdInputProps["onSubmitEditing"] }> = ({ onSubmitEditing }) => {
  return (
    <>
      <PdFormLabel>Zip *</PdFormLabel>
      <PdHookFormInput
        name="zipCode"
        rules={{ required: "Required" }}
        placeholder="100001"
        onSubmitEditing={onSubmitEditing}
      />
      <PdHookFormErrorMessage name="zipCode" />
    </>
  );
};

const PostalCodeInput: FC<{ onSubmitEditing?: tPdInputProps["onSubmitEditing"] }> = ({ onSubmitEditing }) => {
  return (
    <>
      <PdFormLabel>Postal Code *</PdFormLabel>
      <PdHookFormInput
        name="zipCode"
        rules={{ required: "Required" }}
        placeholder="Postal Code"
        onSubmitEditing={onSubmitEditing}
      />
      <PdHookFormErrorMessage name="zipCode" />
    </>
  );
};

const AddressForm: FC<{
  data: tRetailerAddressesFormData | undefined;
  retailerId?: string;
  onSuccessSave: (payload: { data?: tLongAddress[]; mode: "save" | "save-add" }) => void;
}> = ({ data, retailerId, onSuccessSave }) => {
  const rIsMobile = useIsMobileSelector();
  const dispatch = useAppDispatch();
  const formMethods = useForm<tRetailerAddressesFormData>({
    defaultValues: {
      isDefault: !!data?.isDefault,
      name: data?.name || "",
      phone: data?.phone || "",
      country: data?.country || usCountryOption,
      address1: data?.address1 || "",
      address2: data?.address2 || "",
      city: data?.city || "",
      state: data?.state || null,
      zipCode: data?.zipCode || "",
    },
  });
  const country: tCountryOption | null = useWatch({ name: "country", control: formMethods.control });

  const sendFormData = async (formData: tRetailerAddressesFormData): Promise<tLongAddress[] | undefined> => {
    const payload: tAddressPayload = {
      isDefault: formData.isDefault,
      name: formData.name,
      phone: formData.phone,
      address1: formData.address1,
      address2: formData.address2,
      city: formData.city,
      stateCode: formData.state?.value ?? "",
      zipCode: formData.zipCode,
      countryCode: formData.country?.value ?? "US",
    };
    const addresses = formData.id
      ? await dispatch(updateRetailerAddress(formData.id, payload, retailerId))
      : await dispatch(addRetailerAddress(payload, retailerId));

    dispatch({ type: "RIBBON", message: "Address saved" });

    return addresses;
  };

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    try {
      const addresses = await sendFormData(formData);
      onSuccessSave({ data: addresses, mode: "save" });
    } catch (error) {
      dispatch({
        type: "RIBBON",
        message: new Error(
          isApiError(error) && error.response ? error.response?.data.message : "An error occurred please try again.",
        ),
      });
    }
  });

  const handleOnPressSaveAndAdd = formMethods.handleSubmit(async (formData) => {
    try {
      formMethods.reset({
        isDefault: false,
        name: "",
        phone: "",
        address1: "",
        address2: "",
        city: "",
        state: null,
        zipCode: "",
        country: usCountryOption,
      });
      const addresses = await sendFormData(formData);
      onSuccessSave({ data: addresses, mode: "save-add" });
    } catch (error) {
      dispatch({
        type: "RIBBON",
        message: new Error(
          isApiError(error) && error.response ? error.response?.data.message : "An error occurred please try again.",
        ),
      });
    }
  });

  useEffect(() => {
    if (data?.id) {
      formMethods.register("id");
      formMethods.setValue("id", data?.id);
    }
  }, [data?.id, formMethods]);

  return (
    <FormProvider {...formMethods}>
      <View style={styles.form}>
        <PdFormLabel>Default</PdFormLabel>
        <PdHookFormSwitch name="isDefault" style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }} />
      </View>
      <PdStack direction={rIsMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formFirstRow}>
        <View>
          <PdFieldset style={styles.formFieldset}>Store Name</PdFieldset>
          <View>
            <PdHookFormInput
              name="name"
              placeholder="Main St Location"
              rules={{ required: "Required" }}
              onSubmitEditing={handleOnSubmit}
            />
            <PdHookFormErrorMessage name="name" />
          </View>
        </View>
        <View>
          <PdFieldset style={styles.formFieldset}>Store Phone Number</PdFieldset>
          <View>
            <PdHookFormInputPhone
              name="phone"
              placeholder="(XXX) XXX-XXXX"
              autoCompleteType="tel"
              rules={{ required: "Required" }}
              onSubmitEditing={handleOnSubmit}
            />
            <PdHookFormErrorMessage name="phone" />
          </View>
        </View>
      </PdStack>
      <PdFieldset style={styles.formFieldset}>Store Address</PdFieldset>
      <View style={[styles.zIndex1, styles.formSecondRow]}>
        <PdFormLabel>Country *</PdFormLabel>
        <PdHookFormSelect
          name="country"
          rules={{ required: "Required" }}
          placeholder="United States"
          options={[usCountryOption, caCountryOption]}
          isClearable={false}
          onSubmitEditing={handleOnSubmit}
          onChange={() => {
            formMethods.setValue("address1", "");
            formMethods.setValue("address2", "");
            formMethods.setValue("city", "");
            formMethods.setValue("state", null);
            formMethods.setValue("zipCode", "");
          }}
        />
        <PdHookFormErrorMessage name="country" />
      </View>
      <PdStack direction={rIsMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formSecondRow}>
        <View>
          <PdFormLabel>Address *</PdFormLabel>
          <PdHookFormInput
            name="address1"
            rules={{ required: "Required" }}
            placeholder="1 Main Street"
            onSubmitEditing={handleOnSubmit}
          />
          <PdHookFormErrorMessage name="address1" />
        </View>
        <View>
          <PdFormLabel>Address 2</PdFormLabel>
          <PdHookFormInput name="address2" placeholder="Suite 1000" onSubmitEditing={handleOnSubmit} />
        </View>
      </PdStack>
      <PdStack direction={rIsMobile ? "vertical" : "horizontal"} layout={[2, 2]} gap={3} style={styles.formThirdRow}>
        <View>
          <PdFormLabel>City *</PdFormLabel>
          <PdHookFormInput
            name="city"
            rules={{ required: "Required" }}
            placeholder="City"
            onSubmitEditing={handleOnSubmit}
          />
          <PdHookFormErrorMessage name="city" />
        </View>
        <View>
          {country?.value === "US" && <USStatesSelect onSubmitEditing={handleOnSubmit} />}
          {country?.value === "CA" && <CAProvincesSelect onSubmitEditing={handleOnSubmit} />}
        </View>
      </PdStack>
      <View style={styles.formFourthRow}>
        {country?.value === "US" && <ZipCodeInput onSubmitEditing={handleOnSubmit} />}
        {country?.value === "CA" && <PostalCodeInput onSubmitEditing={handleOnSubmit} />}
      </View>
      <PdStack direction={rIsMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formButtonGroup}>
        <PdButton onPress={handleOnSubmit}>
          <PdButtonLabel>Save</PdButtonLabel>
        </PdButton>
        <PdButton onPress={handleOnPressSaveAndAdd} variant="outline">
          <PdButtonLabel>Save & Add Another</PdButtonLabel>
        </PdButton>
      </PdStack>
    </FormProvider>
  );
};

const RetailerAddressesModal: FC<tModalComponentProps<tRetailerAddressesModalOptions, tRetailerAddressesModalResult>> =
  ({ onClose, onResolve, selectedId, retailerId }) => {
    const dispatch = useAppDispatch();
    const confirmModal = useConfirmModal();
    const isMobile = useIsMobile();
    const { isLoading, data, setQueryData } = useQuery<tLongAddress[] | undefined>(
      useCallback(() => (retailerId ? getAdminRetailerAddresses(retailerId) : getRetailerAddresses()), [retailerId]),
    );
    const [addressToEdit, setAddressToEdit] = useState<tRetailerAddressesFormData | undefined>(undefined);

    const handleOnClose = () => {
      onClose();
    };

    const handleOnSelect = (address: tLongAddress) => {
      onResolve(address);
    };

    const handleOnPressAdd = () => {
      setAddressToEdit({
        id: "",
        isDefault: false,
        name: "",
        phone: "",
        address1: "",
        address2: "",
        city: "",
        state: null,
        zipCode: "",
        country: null,
      });
    };

    const handleOnPressEdit = (address: tLongAddress) => {
      const country =
        countryOptions.find((countryOption) =>
          [countryOption.label, countryOption.value].includes(address.country_code),
        ) ?? null;
      setAddressToEdit({
        id: address.id || "",
        isDefault: !!address.isDefault,
        name: address.name || "",
        phone: address.phone || "",
        address1: address.address1,
        address2: address.address2 || "",
        city: address.city,
        state:
          country?.value === "CA"
            ? caProvinceOptions.find((province) =>
                [province.label, province.value].includes(address.state_or_province),
              ) ?? null
            : usStateOptions.find((state) => [state.label, state.value].includes(address.state_or_province)) ?? null,
        zipCode: address.postal_code,
        country,
      });
    };

    const handleOnPressDelete = async (selectedAddress: tLongAddress) => {
      const response = await confirmModal.openAsync({
        title: "Remove Address",
        confirmButtonLabel: "Remove",
      });
      if (response.status === "closed") return;

      if (selectedAddress.id) {
        const addresses = await dispatch(removeRetailerAddress(selectedAddress.id, retailerId));
        setQueryData(addresses);
        dispatch({ type: "RIBBON", message: "Address removed" });
      }
    };

    const handleOnSuccessSave = ({ data: _data, mode }: { data?: tLongAddress[]; mode: "save" | "save-add" }) => {
      if (mode === "save") {
        setAddressToEdit(undefined);
      }
      setQueryData(_data);
    };

    return (
      <PdModal style={styles.modal} onClose={handleOnClose} closeOnOverlayClick={!isMobile}>
        <PdModalCloseButton onPress={handleOnClose} />
        <ScrollView contentContainerStyle={styles.modalBody}>
          <View style={styles.modalHeader}>
            {addressToEdit && (
              <PdIconButton
                onPress={() => setAddressToEdit(undefined)}
                variant="ghost"
                icon={<PdArrowLeft style={styles.arrowLeftIcon} />}
                style={styles.arrowLeftButtonIcon}
              />
            )}
            <PdModalTitle>{getTitle(addressToEdit)}</PdModalTitle>
          </View>
          {isLoading && !addressToEdit && (
            <View style={[styles.widthFull, styles.flex1, styles.alignCenter, styles.justifyCenter]}>
              <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
            </View>
          )}
          {!isLoading && !addressToEdit && (
            <AddressesList
              data={data}
              selectedId={selectedId}
              onSelect={handleOnSelect}
              onAdd={handleOnPressAdd}
              onEdit={handleOnPressEdit}
              onDelete={handleOnPressDelete}
            />
          )}
          {addressToEdit && (
            <AddressForm data={addressToEdit} retailerId={retailerId} onSuccessSave={handleOnSuccessSave} />
          )}
        </ScrollView>
      </PdModal>
    );
  };

const useRetailerAddressesModal = (): tUseModal<tRetailerAddressesModalOptions, tLongAddress> => {
  return useModal<tRetailerAddressesModalOptions, tLongAddress>(RetailerAddressesModal);
};

export default useRetailerAddressesModal;

const styles = StyleSheet.create({
  addressButton: {
    marginBottom: kStyleConst.kSpacing * 4,
    width: "100%",
  },
  addressButtonContent: {
    borderColor: kColors.gray300,
    borderWidth: 1,
    paddingHorizontal: kStyleConst.kSpacing * 3,
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  addressButtonGroup: {
    bottom: 0,
    flexDirection: "row",
    position: "absolute",
    right: 0,
  },
  addressButtonHoveredContent: {
    borderColor: kColors.gray500,
  },
  addressButtonPressedContent: {
    borderColor: kColors.brandDark,
  },
  addressButtonSelectedContent: {
    borderColor: kColors.brandDark,
    shadowColor: kColors.black,
    shadowOffset: { width: 5, height: 5 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
  },
  addressDefaultBadge: {
    alignItems: "center",
    flexDirection: "row",
    position: "absolute",
    right: kStyleConst.kSpacing * 2,
    top: kStyleConst.kSpacing * 2,
  },
  addressDefaultBadgeIcon: {
    height: 18,
    width: 18,
  },
  addressDefaultBadgeLabel: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 12,
    letterSpacing: 1.1,
    lineHeight: 25,
    marginRight: kStyleConst.kSpacing * 2,
  },
  addressIcon: {
    height: 18,
    width: 18,
  },
  addressText: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    letterSpacing: 1.1,
    lineHeight: 25,
  },
  addressTextPrimary: {
    fontWeight: "500",
  },
  alignCenter: {
    alignItems: "center",
  },
  arrowLeftButtonIcon: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  arrowLeftIcon: {
    width: 22,
  },
  flex1: {
    flex: 1,
  },
  form: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 4 * kStyleConst.kSpacing,
    marginLeft: "auto",
  },
  formButtonGroup: {
    width: "100%",
    zIndex: -1,
  },
  formFieldset: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  formFirstRow: {
    marginBottom: 4 * kStyleConst.kSpacing,
    width: "100%",
  },
  formFourthRow: {
    marginBottom: 6 * kStyleConst.kSpacing,
    width: "100%",
    zIndex: -1,
  },
  formSecondRow: {
    marginBottom: 3 * kStyleConst.kSpacing,
    width: "100%",
  },
  formThirdRow: {
    marginBottom: 3 * kStyleConst.kSpacing,
    width: "100%",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  modalHeader: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  widthFull: {
    width: "100%",
  },
  zIndex1: {
    zIndex: 1,
  },
});
